// Runs a schedule change; when the server answers CONFLICTS, asks how to handle each affected booking and retries.
import { type ReactNode, useCallback, useState } from 'react';
import { ApiError } from '../api/client.ts';
import type { Conflict, Resolution } from '../api/types.ts';
import { useShop } from '../app/boot.tsx';
import { message } from '../lib/errors.ts';
import { thaiDateTime } from '../lib/time.ts';
import { Banner, Dialog, useToast } from '../ui/index.tsx';

type Submit = (resolutions?: Record<string, Resolution>) => Promise<unknown>;
type Choice = { action: 'keep' } | { action: 'move'; barberId: string } | { action: 'cancel'; reason: string };

const DEFAULT_REASON = 'ร้านเปลี่ยนตารางเวลา';

export function useConflictRunner(): { run: (submit: Submit, opts?: { allowKeep?: boolean; onDone?: () => void }) => Promise<void>; dialog: ReactNode } {
  const { shop } = useShop();
  const toast = useToast();
  const [pending, setPending] = useState<{ conflicts: Conflict[]; submit: Submit; allowKeep: boolean; onDone?: () => void } | null>(null);
  const [choices, setChoices] = useState<Record<string, Choice>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const conflictsOf = (e: unknown): Conflict[] | null =>
    e instanceof ApiError && e.code === 'CONFLICTS' ? ((e.details?.conflicts as Conflict[] | undefined) ?? []) : null;

  const run = useCallback(async (submit: Submit, opts: { allowKeep?: boolean; onDone?: () => void } = {}) => {
    try {
      await submit(undefined);
      opts.onDone?.();
    } catch (e) {
      const c = conflictsOf(e);
      if (c) {
        setChoices({});
        setError(null);
        setPending({ conflicts: c, submit, allowKeep: opts.allowKeep !== false, onDone: opts.onDone });
      } else {
        toast(message(e), 'error');
      }
    }
  }, [toast]);

  const complete = pending !== null && pending.conflicts.every((c) => {
    const ch = choices[c.booking.id];
    return ch && (ch.action !== 'cancel' || ch.reason.trim() !== '');
  });

  async function confirm() {
    if (!pending) return;
    setBusy(true);
    setError(null);
    const resolutions: Record<string, Resolution> = {};
    for (const c of pending.conflicts) {
      const ch = choices[c.booking.id];
      resolutions[c.booking.id] = ch.action === 'cancel' ? { action: 'cancel', reason: ch.reason.trim() } : ch;
    }
    try {
      await pending.submit(resolutions);
      const done = pending.onDone;
      setPending(null);
      toast('บันทึกแล้ว', 'success');
      done?.();
    } catch (e) {
      const c = conflictsOf(e);
      if (c) setPending({ ...pending, conflicts: c });
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }

  const barberName = (id: string) => shop.barbers.find((b) => b.id === id)?.name ?? id;
  const set = (id: string, ch: Choice) => setChoices((x) => ({ ...x, [id]: ch }));

  const dialog = (
    <Dialog open={pending !== null} title="มีการจองที่ได้รับผลกระทบ" confirmLabel="ยืนยันและบันทึก" loading={busy}
      confirmDisabled={!complete} onConfirm={() => void confirm()} onCancel={() => setPending(null)}>
      {error && <Banner kind="error">{error}</Banner>}
      <p className="muted small" style={{ marginBottom: 12 }}>เลือกวิธีจัดการแต่ละคิว ลูกค้าจะได้รับแจ้งเมื่อย้ายหรือยกเลิก</p>
      <div className="stack">
        {pending?.conflicts.map((c) => {
          const id = c.booking.id;
          const ch = choices[id];
          const name = `res-${id}`;
          return (
            <div key={id} className="card" data-testid={`conflict-${id}`}>
              <div className="card-title">{c.booking.customerName ?? 'ลูกค้า'}</div>
              <p className="muted small">{thaiDateTime(c.booking.startAt)} · {c.booking.serviceName} · ช่าง{barberName(c.booking.barberId)}</p>
              {pending.allowKeep && (
                <label className="check">
                  <input type="radio" name={name} checked={ch?.action === 'keep'} onChange={() => set(id, { action: 'keep' })} />
                  <span>คงไว้</span>
                </label>
              )}
              <label className="check">
                <input type="radio" name={name} disabled={c.moveTargets.length === 0} checked={ch?.action === 'move'}
                  onChange={() => set(id, { action: 'move', barberId: c.moveTargets[0] })} />
                <span>ย้ายไปช่าง</span>
              </label>
              {ch?.action === 'move' && (
                <select aria-label="ช่างที่รับแทน" value={ch.barberId} onChange={(e) => set(id, { action: 'move', barberId: e.target.value })}
                  style={{ minHeight: 40, marginBottom: 8, width: '100%' }}>
                  {c.moveTargets.map((t) => <option key={t} value={t}>{barberName(t)}</option>)}
                </select>
              )}
              {c.moveTargets.length === 0 && <p className="field-msg">ไม่มีช่างว่างเวลานี้</p>}
              <label className="check">
                <input type="radio" name={name} checked={ch?.action === 'cancel'} onChange={() => set(id, { action: 'cancel', reason: DEFAULT_REASON })} />
                <span>ยกเลิกและแจ้งลูกค้า</span>
              </label>
              {ch?.action === 'cancel' && (
                <input aria-label="เหตุผลที่แจ้งลูกค้า" value={ch.reason} maxLength={200} onChange={(e) => set(id, { action: 'cancel', reason: e.target.value })}
                  style={{ minHeight: 40, width: '100%', padding: '0 10px', border: '1.5px solid var(--line-strong)', borderRadius: 10 }} />
              )}
            </div>
          );
        })}
      </div>
    </Dialog>
  );

  return { run, dialog };
}
